'use client';

import { useField } from 'formik';
import { useMemo, ReactNode } from 'react';
import {
  Radio,
  FormLabel,
  RadioGroup,
  IconButton,
  Typography,
  FormControl,
  FormHelperText,
} from '@mui/material';

import { controlError } from 'utils/error';
import { questionLabelText } from 'utils/question-label';
import { QuestionLabel } from 'components/question-label';

export interface ScaleRatingFieldProps {
  min: number;
  max: number;
  name: string;
  label?: string;
  ariaLabel?: string;
  header?: ReactNode;
  stretchLayout?: boolean;
}

export const ScaleRatingField = ({
  min,
  max,
  name,
  label,
  header,
  ariaLabel,
  stretchLayout = false,
}: ScaleRatingFieldProps) => {
  const [field, meta, helpers] = useField<number | null>(name);

  const errorText = controlError(meta, name, label);
  const hasError = Boolean(errorText);

  const { width, items } = useMemo(() => {
    const items = [...Array(max - min + 1)].map((_, i) => min + i);
    const width = items.length * 32 + (items.length - 1) * 10;
    return { width, items };
  }, [min, max]);

  return (
    <FormControl error={hasError}>
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        {label && (
          <FormLabel
            component="div"
            sx={{
              minWidth: 0,
              whiteSpace: 'pre-line',
              overflowWrap: 'anywhere',
            }}
          >
            <QuestionLabel value={label} />
          </FormLabel>
        )}

        <div
          style={stretchLayout ? undefined : { width }}
          className={stretchLayout ? 'w-full shrink-0 md:w-1/2' : 'shrink-0'}
        >
          {header}
          {stretchLayout ? (
            <RadioGroup
              row
              name={`${name}-group`}
              className="flex w-full"
              aria-label={questionLabelText(ariaLabel ?? label)}
              value={field.value == null ? '' : String(field.value)}
              onChange={(_, selectedValue) =>
                helpers.setValue(Number(selectedValue), true)
              }
            >
              {items.map((value) => (
                <div key={value} className="flex flex-1 justify-center">
                  <Radio
                    value={String(value)}
                    slotProps={{
                      input: {
                        'aria-label': `${value} out of ${max}`,
                      },
                    }}
                  />
                </div>
              ))}
            </RadioGroup>
          ) : (
            <div
              role="group"
              className="flex gap-2.5"
              aria-label={questionLabelText(ariaLabel ?? label)}
            >
              {items.map((value) => {
                const isSelected = field.value === value;
                return (
                  <IconButton
                    key={value}
                    aria-pressed={isSelected}
                    aria-label={`${value} out of ${max}`}
                    onClick={() => helpers.setValue(value, true)}
                    sx={{
                      width: 32,
                      height: 32,
                      border: '1px solid',
                      color: isSelected ? 'white' : 'text.primary',
                      borderColor: isSelected ? 'primary.main' : 'grey.300',
                      backgroundColor: isSelected
                        ? 'primary.main'
                        : 'transparent',
                      '&:hover': {
                        backgroundColor: isSelected
                          ? 'primary.dark'
                          : 'grey.100',
                      },
                    }}
                  >
                    <Typography>{value}</Typography>
                  </IconButton>
                );
              })}
            </div>
          )}
        </div>
      </div>
      {hasError && (
        <FormHelperText className="!ml-0">{errorText}</FormHelperText>
      )}
    </FormControl>
  );
};
